/* global __APP_VERSION__ */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import CheckBold from '@assets/check-bold.svg?react'
import { Rating } from '@mui/material'
import { Assets, Button, Select, SelectItem, Star, StarFilled, TextField } from '@nexds/web'
import styled, { useTheme } from 'styled-components'

import { ReviewProps, SendReviewUseCase } from '@/domain/useCases/General/SendReviewUseCase'

import { ReviewSentProps } from '../types/FeedbackCollector'

import { useI18n } from '@/presentation/hooks/useI18n'
import { useScreenDimensions } from '@/presentation/hooks/useScreenDimensions'
import { getStateColor } from '@/presentation/utils'
import { appZIndex } from '@/presentation/utils/zIndexMapper'

// TODO: I18n
function FeedbackCollectorPresenter() {
  const theme = useTheme()
  const { t } = useI18n()
  const VERSION = __APP_VERSION__
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm()

  const { screenWidth } = useScreenDimensions()

  const [ratingValue, setRatingValue] = useState(0)
  const [feedbackTypeSelected, setFeedbackTypeSelected] = useState<ReviewProps['type']>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [reviewSent, setReviewSent] = useState<ReviewSentProps>({
    wasSent: false,
    isSuccess: false
  })

  const headerMessage = useMemo(() => {
    if (reviewSent.wasSent) {
      if (reviewSent.isSuccess)
        return (
          <>
            <CheckBold />
            {t('FEEDBACK_COLLECTOR_HEADER_MESSAGE_FEEDBACK-SENT')}
          </>
        )
      else return `⚠️ ${t('FEEDBACK_COLLECTOR_HEADER_MESSAGE_FEEDBACK-ERROR')}`
    } else if (feedbackTypeSelected === 'bug') return t('FEEDBACK_COLLECTOR_HEADER_TITLE_BUG')
    else if (feedbackTypeSelected === 'suggestion') return t('FEEDBACK_COLLECTOR_HEADER_TITLE_SUGGESTION')
    else return t('FEEDBACK_COLLECTOR_HEADER_TITLE_MAIN')
  }, [feedbackTypeSelected, reviewSent])

  const sendReview = async (reviewBody: ReviewProps) => {
    const result = await new SendReviewUseCase().execute(reviewBody)
    if (result.isFailure) {
      console.error(result.error)
      return false
    }
    return true
  }

  const onSubmit = async (data: FieldValues) => {
    const body: ReviewProps = {
      rating: feedbackTypeSelected === 'suggestion' ? ratingValue : null,
      type: feedbackTypeSelected,
      feature: 'general',
      comment: feedbackTypeSelected === 'suggestion' ? data['comment-suggestion'] : data['comment-bug'], // TODO: separar por tipo
      version: VERSION,
      product: 'planning-app'
    }
    const isSuccess = await sendReview(body)

    if (isSuccess) {
      reset()
      setRatingValue(0)
    }
    setReviewSent({ wasSent: true, isSuccess })
  }

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setFeedbackTypeSelected(null)
    setReviewSent({ wasSent: false, isSuccess: false })
  }, [])

  useEffect(() => {
    setReviewSent({ isSuccess: false, wasSent: false })
  }, [feedbackTypeSelected])

  const reviewButtonLabel = useMemo(() => {
    if (isOpen) {
      if (screenWidth <= 620) {
        return <Assets.Close2 size={16} color={theme.colors.neutralL5} />
      } else {
        return t('FEEDBACK_COLLECTOR_MAIN-BUTTON_LABEL_CLOSE')
      }
    } else if (screenWidth <= 620) {
      return '📢'
    } else {
      return `📢 ${t('FEEDBACK_COLLECTOR_MAIN-BUTTON_LABEL_SEND')}`
    }
  }, [screenWidth, isOpen])

  return (
    <Container isOpen={isOpen}>
      <ReviewButton onClick={() => setIsOpen((prev) => !prev)}>{reviewButtonLabel}</ReviewButton>
      {isOpen && (
        <ReviewPopupContainer>
          <Header>
            <Title>{headerMessage}</Title>
            <ActionButton onClick={handleClose}>
              <Assets.Close size={32} color={theme.colors.secondaryL3} />
            </ActionButton>
          </Header>
          <Body flexDirection={!feedbackTypeSelected ? 'row' : 'column'} gap={!feedbackTypeSelected ? 32 : 16}>
            {reviewSent.wasSent ? (
              <>
                <Message>
                  {reviewSent.isSuccess ? (
                    <>
                      Obrigado!
                      <br />
                      <br />
                      Sua opinião faz toda a diferença para que possamos continuar melhorando.
                    </>
                  ) : (
                    <>Parece que um erro inesperado aconteceu. Tente novamente mais tarde.</>
                  )}
                </Message>
                <Button
                  label={reviewSent.isSuccess ? 'Fechar' : 'Voltar'}
                  color="secondary"
                  variant="filled"
                  size="sm"
                  onPress={
                    reviewSent.isSuccess ? handleClose : () => setReviewSent((prev) => ({ ...prev, wasSent: false }))
                  }
                />
                {reviewSent.isSuccess && (
                  <Button
                    label="Enviar outro feedback"
                    color="secondary"
                    variant="outline"
                    size="sm"
                    onPress={() => {
                      setFeedbackTypeSelected(null)
                      setReviewSent({ wasSent: false, isSuccess: false })
                    }}
                  />
                )}
              </>
            ) : feedbackTypeSelected === 'bug' ? (
              <>
                <InputContainer>
                  <TextField
                    autoFocus
                    multiline
                    maxLength={1000}
                    helpMessage={errors?.comment ? 'Descreva o problema' : ''}
                    error={!!errors?.comment}
                    placeholder="Tente ser breve e direto, relatando as etapas que você realizou e que resultaram no problema."
                    {...register('comment-bug', { required: true })}
                  />
                </InputContainer>
                <Button
                  label="Enviar"
                  color="secondary"
                  variant="filled"
                  size={'sm'}
                  onPress={handleSubmit(onSubmit)}
                />
              </>
            ) : feedbackTypeSelected === 'suggestion' ? (
              <>
                <RatingContainer>
                  <RatingLabel>Avaliação:</RatingLabel>
                  <Rating
                    icon={<StarFilled color={theme.colors.yellowL0} size={20} />}
                    emptyIcon={<StarFilled color={theme.colors.secondaryL1} size={20} />}
                    name="rating-stars"
                    value={ratingValue}
                    onChange={(_, newValue) => {
                      setRatingValue(newValue)
                    }}
                  />
                </RatingContainer>
                <InputContainer>
                  <TextField
                    autoFocus
                    multiline
                    helpGutter={true}
                    labelGutter={false}
                    maxLength={1000}
                    helpMessage={errors?.comment ? 'Escreva um feedback' : ''}
                    error={!!errors?.comment}
                    placeholder="Como está sendo a sua experiência com a atualização do aplicativo web?"
                    {...register('comment-suggestion', { required: true })}
                  />
                </InputContainer>
                <Button
                  label="Enviar"
                  color="secondary"
                  variant="filled"
                  size={'sm'}
                  onPress={handleSubmit(onSubmit)}
                />
              </>
            ) : (
              <TypeWrapper>
                <TypeSelector>
                  <TypeButton onClick={() => setFeedbackTypeSelected('bug')}>
                    <Assets.Warning size={36} color={theme.colors.neutralL5} />
                  </TypeButton>
                  <TypeLabel>Problema</TypeLabel>
                </TypeSelector>
                <TypeSelector>
                  <TypeButton onClick={() => setFeedbackTypeSelected('suggestion')}>
                    <Assets.News size={36} color={theme.colors.neutralL5} />
                  </TypeButton>
                  <TypeLabel>Comentário</TypeLabel>
                </TypeSelector>
              </TypeWrapper>
            )}
          </Body>
        </ReviewPopupContainer>
      )}
    </Container>
  )
}

export { FeedbackCollectorPresenter }

interface ContainerProps {
  isOpen?: boolean
}

const Container = styled.div<ContainerProps>`
  position: absolute;
  z-index: ${({ isOpen }) => (isOpen ? appZIndex.CHAT : appZIndex.FEEDBACK_COLLECTOR)};
  bottom: 23px;
  right: 10px;

  &:hover {
    z-index: ${appZIndex.CHAT + 1};
  }

  @media (max-height: 400px) {
    bottom: 23px;
    right: 60px;
  }
`

const ReviewButton = styled.button`
  border: none;
  cursor: pointer;
  height: 43px;
  padding: 14px 19px;
  border-radius: 25px;
  background-color: ${({ theme }) => theme.colors.secondaryD1};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: -2px 4px 6px 0px ${({ theme }) => `${theme.colors.secondary}80`};
  ${({ theme }) => theme.fonts.titleXS}
`

const ReviewPopupContainer = styled.div`
  position: absolute;
  bottom: calc(100% + 16px);
  right: -1px;

  width: 500px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.secondaryL2};

  background-color: ${({ theme }) => theme.colors.secondaryD1};
  box-shadow: -2px 4px 6px 0px ${({ theme }) => `${theme.colors.secondary}80`};

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 510px) {
    width: 100vw;
    bottom: -23px;
    right: -10px;
    border-radius: 0;
    border: none;
  }
`

const Header = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondaryL2};

  height: 80px;
  padding: 24px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.h2`
  ${({ theme }) => theme.fonts.titleMD}
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 350px) {
    ${({ theme }) => theme.fonts.titleSM}
  }
`

const ActionButton = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

interface BodyProps {
  flexDirection: 'row' | 'column'
  gap: number
}

const Body = styled.div<BodyProps>`
  padding: 16px;
  display: flex;
  flex-direction: ${({ flexDirection }) => flexDirection};
  justify-content: center;
  align-items: stretch;
  gap: ${({ gap }) => gap}px;
  height: 100%;
  min-height: 200px;
  overflow: auto;
`

const RatingContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

const RatingLabel = styled.h5`
  ${({ theme }) => theme.fonts.h5}
`

const InputContainer = styled.div`
  border-radius: 10px;

  & textarea {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 112px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      border-radius: 20px;
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
      border-radius: 20px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.secondaryL3};
      border-radius: 20px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: ${({ theme }) => theme.colors.secondaryL3};
      cursor: grab;
    }

    &::-webkit-scrollbar-thumb:active {
      cursor: grabbing;
    }
  }
`

const Message = styled.p`
  ${({ theme }) => theme.fonts.h5}
`

const TypeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
`

const TypeSelector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
`

const TypeButton = styled.button`
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  width: 112px;
  height: 112px;
  background-color: ${({ theme }) => theme.colors.secondary};
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${({ theme }) => getStateColor(theme.colors.secondary, 'hover')};
  }

  @media (max-width: 350px) {
    width: 70px;
    height: 70px;
    border-radius: 12px;

    & svg {
      width: 28px;
      height: 28px;
    }
  }
`

const TypeLabel = styled.p`
  ${({ theme }) => theme.fonts.titleXS}
  text-align: center;

  @media (max-width: 350px) {
    ${({ theme }) => theme.fonts.infoSM}
  }
`
